import { useEffect, useMemo, useState } from "react";
import type {
    Ticket,
    Chip,
    EventLite,
    FunctionLite,
    ShiftLite,
    TargetType,
    TicketAssignment,
} from "../types";
import { toLocalInputValue } from "../utils/date";
import {
    assignTargetApi,
    deleteAssignmentApi,
    deleteTicketApi,
    fetchAssignmentsApi,
    fetchTicketsApi,
    saveTicketApi,
} from "../api/ticketsApi";
import { fetchEventsApi, fetchShiftsForEventApi } from "../api/eventsApi";
import { fetchFunctionsApi } from "../api/functionsApi";

type ConfirmFn = (opts: {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    tone?: "danger" | "primary";
}) => Promise<boolean>;

type ToastFn = {
    success: (msg: string, title?: string) => void;
    error: (msg: string, title?: string) => void;
    info: (msg: string, title?: string) => void;
};

export const useTicketsBoard = (deps?: { confirm?: ConfirmFn; toast?: ToastFn }) => {
    const confirm = deps?.confirm;
    const toast = deps?.toast;

    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [assignments, setAssignments] = useState<TicketAssignment[]>([]);
    const [hideExpired, setHideExpired] = useState(true);

    const [ticketSearch, setTicketSearch] = useState("");
    const [showActiveOnly, setShowActiveOnly] = useState(false);

    const [tab, setTab] = useState<TargetType>("person");
    const [personSearch, setPersonSearch] = useState("");

    const [events, setEvents] = useState<EventLite[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number | "">("");
    const [eventsLoading, setEventsLoading] = useState(false);

    const [shifts, setShifts] = useState<ShiftLite[]>([]);
    const [shiftsLoading, setShiftsLoading] = useState(false);

    const [functions, setFunctions] = useState<FunctionLite[]>([]);
    const [functionSearch, setFunctionSearch] = useState("");
    const [functionsLoading, setFunctionsLoading] = useState(false);
    const [functionsError, setFunctionsError] = useState<string | null>(null);

    const refreshTickets = async (eventId?: number) => {
        if (!eventId) {
            setTickets([]);
            return;
        }

        setTickets(await fetchTicketsApi(eventId));
    };
    const refreshAssignments = async () => {
        if (selectedEventId === "") {
            setAssignments([]);
            return;
        }

        const data = await fetchAssignmentsApi(
            Number(selectedEventId)
        );

        setAssignments(data);
    };

    const refreshEvents = async () => {
        setEventsLoading(true);
        try {
            const ev = await fetchEventsApi();
            setEvents(ev);

            if (ev.length && selectedEventId === "") {
                setSelectedEventId(ev[0].eventID);
            }
        } finally {
            setEventsLoading(false);
        }
    };

    const refreshShiftsForEvent = async (eventId: number) => {
        setShiftsLoading(true);
        try {
            setShifts(await fetchShiftsForEventApi(eventId));
        } finally {
            setShiftsLoading(false);
        }
    };

    const refreshFunctions = async (eventId: number) => {
        setFunctionsLoading(true);
        setFunctionsError(null);

        try {
            setFunctions(await fetchFunctionsApi(eventId));
        } catch (e: any) {
            console.error(e);
            setFunctionsError(e?.message || "Fejl ved hentning af funktioner");
            setFunctions([]);
        } finally {
            setFunctionsLoading(false);
        }
    };

    useEffect(() => {
        refreshEvents();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedEventId === "") {
            setTickets([]);
            setAssignments([]);
            return;
        }

        const loadEventData = async () => {
            const eventId = Number(selectedEventId);

            await Promise.all([
                refreshTickets(eventId),
                refreshAssignments(),
            ]);
        };

        loadEventData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventId]);

    useEffect(() => {
        if ((tab === "shift" || tab === "function") && events.length === 0 && !eventsLoading) {
            refreshEvents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    useEffect(() => {
        if (tab !== "shift") return;
        if (selectedEventId === "") return;

        refreshShiftsForEvent(Number(selectedEventId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, selectedEventId]);

    useEffect(() => {
        if (tab !== "function") return;
        if (selectedEventId === "") return;

        refreshFunctions(Number(selectedEventId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, selectedEventId]);

    const filteredTickets = useMemo(() => {
        const q = ticketSearch.trim().toLowerCase();
        const now = new Date();

        return tickets
            .filter((t) => (showActiveOnly ? t.active : true))
            .filter((t) => {
                if (!hideExpired) return true;

                const end = new Date(t.ticketTimeEnd);
                if (Number.isNaN(end.getTime())) return true;

                return end >= now;
            })
            .filter((t) => (q ? (t.ticketDescription || "").toLowerCase().includes(q) : true))
            .sort(
                (a, b) =>
                    new Date(a.ticketTimeStart).getTime() -
                    new Date(b.ticketTimeStart).getTime()
            );
    }, [tickets, ticketSearch, showActiveOnly, hideExpired]);

    const filteredFunctions = useMemo(() => {
        const q = functionSearch.trim().toLowerCase();
        if (!q) return functions;

        return functions.filter((f) => {
            return (
                (f.functionName || "").toLowerCase().includes(q) ||
                String(f.functionId).includes(q)
            );
        });
    }, [functions, functionSearch]);

    const assignmentsByPerson = useMemo(() => {
        const map = new Map<number, TicketAssignment[]>();

        for (const a of assignments) {
            if (!a.personID) continue;

            if (!map.has(a.personID)) {
                map.set(a.personID, []);
            }

            map.get(a.personID)!.push(a);
        }

        return map;
    }, [assignments]);

    const assignmentsByShift = useMemo(() => {
        const map = new Map<number, TicketAssignment[]>();

        for (const a of assignments) {
            if (!a.shiftsID) continue;

            if (!map.has(a.shiftsID)) {
                map.set(a.shiftsID, []);
            }

            map.get(a.shiftsID)!.push(a);
        }

        return map;
    }, [assignments]);

    const assignmentsByFunction = useMemo(() => {
        const map = new Map<number, TicketAssignment[]>();

        for (const a of assignments) {
            if (!a.functionID) continue;

            if (!map.has(a.functionID)) {
                map.set(a.functionID, []);
            }

            map.get(a.functionID)!.push(a);
        }

        return map;
    }, [assignments]);

    const onCreateTicket = () => {
        if (selectedEventId === "") {
            toast?.error("Vælg et event først.");
            return;
        }

        setSelectedTicket({
            eventID: Number(selectedEventId),
            ticketDescription: "",
            ticketTimeStart: toLocalInputValue(new Date().toISOString()),
            ticketTimeEnd: toLocalInputValue(
                new Date(Date.now() + 60 * 60 * 1000).toISOString()
            ),
            active: true,
        });
    };

    const onSaveTicket = async (ticket: Ticket) => {
        const wasNew = !ticket.ticketID;

        await saveTicketApi(ticket);

        setSelectedTicket(null);
        toast?.success(wasNew ? "Ticket blev oprettet." : "Ticket blev opdateret.");

        if (selectedEventId !== "") {
            await refreshTickets(Number(selectedEventId));
        }
    };

    const onDeleteTicket = async (ticketID: number) => {
        if (!confirm) throw new Error("confirm() not provided to useTicketsBoard");

        const ok = await confirm({
            title: "Slet ticket",
            message: "Er du sikker på at du vil slette denne ticket?",
            confirmText: "Slet",
            cancelText: "Annuller",
            tone: "danger",
        });

        if (!ok) return;

        await deleteTicketApi(ticketID);

        toast?.success("Ticket blev slettet.");
        await refreshTickets();
    };

    const onDeleteAssignment = async (ticketAssignmentID: number) => {
        const ok = confirm
            ? await confirm({
                title: "Fjern billet",
                message: "Er du sikker på at du vil fjerne denne billet fra målgruppen?",
                confirmText: "Fjern",
                cancelText: "Annuller",
                tone: "danger",
            })
            : window.confirm("Fjern denne billet?");

        if (!ok) return;

        const deleted = await deleteAssignmentApi(ticketAssignmentID);

        if (!deleted) {
            toast?.error("Kunne ikke fjerne billet. Prøv igen.");
            return;
        }

        toast?.success("Billetten blev fjernet.");
        await refreshAssignments();
    };

    const assignTicketTo = async (
        ticketID: number,
        targetType: "person" | "shift" | "function",
        targetID: number
    ) => {
        const result = await assignTargetApi(ticketID, targetType, targetID);

        if (!result.ok) {
            toast?.error(result.message || "Kunne ikke tildele ticket");
            return false;
        }

        toast?.success("Ticket blev tildelt.");
        await refreshAssignments();

        return true;
    };

    const personChipsById = useMemo(() => {
        const map = new Map<number, Chip[]>();

        for (const [personID, rows] of assignmentsByPerson.entries()) {
            const chips: Chip[] = rows.map((a) => ({
                id: a.ticketAssignmentID,
                label: a.usedAt
                    ? `${a.ticketDescription} (used)`
                    : a.active
                        ? a.ticketDescription
                        : `${a.ticketDescription} (inactive)`,
                dim: !!a.usedAt || !a.active,
                onDelete: onDeleteAssignment,
            }));

            map.set(personID, chips);
        }

        return map;
    }, [assignmentsByPerson]);

    const shiftChipsById = useMemo(() => {
        const map = new Map<number, Chip[]>();

        for (const s of shifts) {
            const rows = assignmentsByShift.get(s.shiftsID) ?? [];

            const chips: Chip[] = rows.map((a) => ({
                id: a.ticketAssignmentID,
                label: a.active ? a.ticketDescription : `${a.ticketDescription} (inactive)`,
                dim: !a.active,
                onDelete: onDeleteAssignment,
            }));

            map.set(s.shiftsID, chips);
        }

        return map;
    }, [shifts, assignmentsByShift]);

    const functionChipsById = useMemo(() => {
        const map = new Map<number, Chip[]>();

        for (const f of filteredFunctions) {
            const rows = assignmentsByFunction.get(f.functionId) ?? [];

            const chips: Chip[] = rows.map((a) => ({
                id: a.ticketAssignmentID,
                label: a.active ? a.ticketDescription : `${a.ticketDescription} (inactive)`,
                dim: !a.active,
                onDelete: onDeleteAssignment,
            }));

            map.set(f.functionId, chips);
        }

        return map;
    }, [filteredFunctions, assignmentsByFunction]);

    return {
        filteredTickets,
        selectedTicket,
        setSelectedTicket,
        ticketSearch,
        setTicketSearch,
        showActiveOnly,
        setShowActiveOnly,
        hideExpired,
        setHideExpired,

        tab,
        setTab,

        personSearch,
        setPersonSearch,
        personChipsById,

        events,
        eventsLoading,
        selectedEventId,
        setSelectedEventId,

        shifts,
        shiftsLoading,
        refreshShifts: () =>
            selectedEventId ? refreshShiftsForEvent(Number(selectedEventId)) : undefined,
        shiftChipsById,

        functionsLoading,
        functionsError,
        filteredFunctions,
        functionSearch,
        setFunctionSearch,
        functionChipsById,
        refreshFunctions,

        onCreateTicket,
        onSaveTicket,
        onDeleteTicket,
        assignTicketTo,
    };
};